import React, { useMemo, useState } from "react";
import { useDebounce } from "frontend/hooks/debounce";
import LoadedState from "frontend/state/loadedState";
import { FixedSizeList } from "react-window";
import { Assignee } from "shared/datamodel/schemas/task";
import { useAtomValue } from "jotai";
import { usersAtom, usersStateAtom } from "state-atoms/users-atoms";
import { SearchIcon } from "frontend/icons/search";
import CloseIcon from "frontend/ui-components/design-system/icons/close-icon";
import styles from "./task-assignee-picker.module.css";

interface TaskAssigneePickerProps {
  assignee?: Assignee | null;
  onSelected: (selectedUser: Assignee | null) => void;
}

export function TaskAssigneePicker({ assignee, onSelected }: TaskAssigneePickerProps) {
  const users = useAtomValue(usersAtom);
  const usersState = useAtomValue(usersStateAtom);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const usersIdAsArray = [...users.keys()];
  const hasAssignee = !!assignee;

  const nonAssigned = hasAssignee ? usersIdAsArray.filter((id) => id !== assignee.id) : usersIdAsArray;

  const filteredPeople = useMemo(
    () =>
      nonAssigned.filter((id) => {
        const user = users.get(id);
        return user ? user.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) : false;
      }),
    [nonAssigned, debouncedSearchTerm, users]
  );

  const maxListHeight = hasAssignee ? 160 : 205;
  const itemHeight = 26;
  const listHeight = Math.min(filteredPeople.length * itemHeight, maxListHeight);

  return (
    <div className={styles.container}>
      <div className={styles.peopleList}>
        {hasAssignee && (
          <div className={styles.currentAssigneeSection}>
            <div className={styles.currentAssigneeContainer}>
              <div className={styles.currentAssigneeInfo}>
                <img width={17} height={17} className={styles.userPhoto} src={assignee.thumbnail} alt={assignee.name} />
                <span className={styles.userName}>{assignee.name}</span>
              </div>
              <button
                className={styles.clearAssigneeButton}
                onClick={() => onSelected(null)}
                title="Clear assignee"
                type="button"
              >
                <CloseIcon width={8} height={8} />
              </button>
            </div>
          </div>
        )}

        <div className={styles.searchInputContainer}>
          <SearchIcon className={styles.searchIcon} color="#848199" />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        {usersState === LoadedState.loading && <div className={styles.loadingState}>Loading...</div>}
        {usersState === LoadedState.failed && <div className={styles.errorState}>Failed to load members list.</div>}
        {usersState === LoadedState.finished && (
          <FixedSizeList
            height={listHeight}
            width="100%"
            itemCount={filteredPeople.length}
            itemSize={itemHeight}
            itemKey={(index: number) => filteredPeople[index]}
            className={styles.usersList}
          >
            {({ index, style }: { index: number; style: React.CSSProperties }) => (
              <button
                style={style}
                key={filteredPeople[index]}
                data-id={filteredPeople[index]}
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  const id = e.currentTarget.dataset.id;
                  if (!id) return;
                  const userId = Number.parseInt(id, 10);
                  const selectedUser = users.get(userId);
                  if (!selectedUser) {
                    return;
                  }
                  const assigneeData: Assignee = {
                    id: Number(selectedUser.id),
                    name: selectedUser.name,
                    email: selectedUser.email,
                    thumbnail: selectedUser.thumbnail ?? selectedUser.photo ?? "",
                  };
                  onSelected(assigneeData);
                  setSearchTerm("");
                }}
                className={styles.userItem}
              >
                {(() => {
                  const user = users.get(filteredPeople[index]);
                  if (!user) return null;
                  const { name, thumbnail } = user;

                  return (
                    <div className={styles.userContainer}>
                      <img width={17} height={17} className={styles.userPhoto} src={thumbnail} alt={name} />
                      <span className={styles.userName}>{name}</span>
                    </div>
                  );
                })()}
              </button>
            )}
          </FixedSizeList>
        )}
      </div>
    </div>
  );
}
